import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { OrderModel } from 'lib/model/objects/orderModel';
import { ColumnService } from 'lib/services/directory';
import { exists } from 'lib/types';
import {
  OrderManualSyncRequestEvent,
  ORDER_MANUAL_SYNC_REQUEST
} from 'lib/types/events';
import { ResponseOrError, wrapError, wrapSuccess } from 'lib/types/responses';
import { isPublisherOrganization } from 'lib/utils/organizations';
import { getOrThrow } from 'lib/utils/refs';
import { isPublisher } from 'lib/utils/users';
import { selectUser, selectActiveOrganization } from 'redux/auth';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import ToastActions from 'redux/toast';
import { logAndCaptureException } from 'utils';

type EnabledManualSyncRequest = {
  enabled: true;
  callback: () => Promise<ResponseOrError<void>>;
};
type DisabledManualSyncRequest = {
  enabled: false;
};

type ManualSyncRequest = EnabledManualSyncRequest | DisabledManualSyncRequest;

export const useManualSyncRequest = (order: OrderModel): ManualSyncRequest => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const activeOrganization = useAppSelector(selectActiveOrganization);

  const {
    value: enabled,
    isError,
    isLoading
  } = useAsyncEffect<boolean>({
    fetchData: async () => {
      if (!exists(user) || !exists(activeOrganization)) {
        return false;
      }

      if (!isPublisher(user)) {
        return false;
      }

      if (!isPublisherOrganization(activeOrganization)) {
        return false;
      }

      const { orderExport } = activeOrganization.data();

      if (!orderExport) {
        return false;
      }

      const configurationRef = orderExport[order.modelData.product];

      if (!configurationRef) {
        return false;
      }

      const configuration = await getOrThrow(configurationRef);

      return !!configuration.data().triggers[ORDER_MANUAL_SYNC_REQUEST];
    },
    initialData: null,
    dependencies: [order.id, user?.id, activeOrganization?.id]
  });

  if (!user || !activeOrganization || isLoading || !enabled || isError) {
    return { enabled: false };
  }

  const callback = async (): Promise<ResponseOrError<void>> => {
    // Create a manual sync request event
    const [error] = await order.createEvent<OrderManualSyncRequestEvent>({
      type: ORDER_MANUAL_SYNC_REQUEST,
      data: {
        requestedBy: user.ref,
        publisher: activeOrganization.ref
      },
      handleSideEffects: true
    });

    if (error) {
      logAndCaptureException(
        ColumnService.INTEGRATIONS,
        error,
        'Failed to request sync',
        {
          orderId: order.id,
          userId: user.id,
          publisherId: activeOrganization.id
        }
      );
      dispatch(
        ToastActions.toastError({
          headerText: 'Failed to request sync',
          bodyText:
            'Please try requesting to sync again; if the problem persists, please contact support.'
        })
      );
      return wrapError(error);
    }

    // Send a toast notification
    dispatch(
      ToastActions.toastSuccess({
        headerText: 'Sync requested!',
        bodyText: `We will now attempt to sync this order to your publishing system.`
      })
    );
    return wrapSuccess(undefined);
  };

  return { enabled, callback };
};
